import { useEffect, useState } from 'react';
import API_ENDPOINTS from '../../config/apiConfig';
import { api } from '../../utils/api';

/*
 * Help content is served by the API (azfApi/helpCenter) so the /help page and
 * the AI assistant always read the same articles. The whole corpus is small;
 * it is fetched once per session and shared by every Help Center view.
 */

export const topicHref = (topic) => `/help/${topic}`;
export const articleHref = (topic, slug) => `/help/${topic}/${slug}`;

export const KIND_LABEL = {
  howto: 'How-to',
  concept: 'Explainer',
  faq: 'FAQ',
};

let corpusPromise = null;

function loadCorpus() {
  if (!corpusPromise) {
    corpusPromise = api.get(API_ENDPOINTS.HELP_CENTER).then((r) => r.data).catch((err) => {
      corpusPromise = null; // let the next mount retry
      throw err;
    });
  }
  return corpusPromise;
}

/** { topics, loading, error, retry } */
export function useHelpCorpus() {
  const [state, setState] = useState({ topics: null, error: null });
  const [attempt, setAttempt] = useState(0);

  useEffect(() => {
    let live = true;
    loadCorpus()
      .then((data) => { if (live) setState({ topics: data.topics || [], error: null }); })
      .catch((error) => { if (live) setState({ topics: null, error }); });
    return () => { live = false; };
  }, [attempt]);

  return {
    topics: state.topics,
    loading: !state.topics && !state.error,
    error: state.error,
    retry: () => { setState({ topics: null, error: null }); setAttempt((n) => n + 1); },
  };
}

const searchCache = new Map();

/** Ranked results from the API (same ranking the assistant uses), debounced. */
export function useHelpSearch(query, delay = 180) {
  const q = (query || '').trim();
  const [state, setState] = useState({ q: '', results: [], loading: false, error: null });

  useEffect(() => {
    if (!q) { setState({ q: '', results: [], loading: false, error: null }); return undefined; }
    if (searchCache.has(q)) { setState({ q, results: searchCache.get(q), loading: false, error: null }); return undefined; }
    let live = true;
    setState((s) => ({ ...s, loading: true }));
    const t = setTimeout(() => {
      api.get(API_ENDPOINTS.HELP_CENTER + '/search', { params: { q } })
        .then((r) => {
          const results = r.data?.results || [];
          searchCache.set(q, results);
          if (live) setState({ q, results, loading: false, error: null });
        })
        .catch((error) => { if (live) setState({ q, results: [], loading: false, error }); });
    }, delay);
    return () => { live = false; clearTimeout(t); };
  }, [q, delay]);

  return state;
}

/** Ask the help agent. history = prior [{ role, content }] turns of this thread. */
export async function askHelp({ question, history = [], route = null }) {
  const res = await api.post(`${API_ENDPOINTS.HELP_CENTER}/ask`, { question, history, route });
  return res.data;
}
